import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  FolderKanban,
  Clock,
  AlertTriangle,
  CheckCircle2,
  DollarSign,
  Plus,
  ArrowRight,
  ListTodo,
  Target,
} from 'lucide-react';

interface DashboardData {
  summary: {
    activeProjects: number;
    totalProjects: number;
    overdueTasks: number;
    totalTasks: number;
    completedTasks: number;
    totalLoggedHours: number;
    billableHours: number;
    billableAmount: number;
    utilizationRate: number;
    budgetBurnRate: number;
    openRisks: number;
    highRisks: number;
  };
  recentProjects: Array<{
    id: string;
    projectNumber: number;
    name: string;
    status: string;
    projectType: string;
    completionPct: number;
    startDate: string;
    targetEndDate: string;
    accountName: string | null;
    projectManagerName: string | null;
    taskCount: number;
    completedTaskCount: number;
    totalLoggedHours: number;
  }>;
  milestones: Array<{
    id: string;
    projectId: string;
    name: string;
    status: string;
    milestoneDate: string;
    completionPct: number;
    projectName: string;
  }>;
  overdueTasks: Array<{
    id: string;
    title: string;
    status: string;
    priority: string;
    dueDate: string;
    assigneeName: string | null;
    projectName: string;
  }>;
}

const statusVariant: Record<string, 'default' | 'success' | 'warning' | 'destructive' | 'secondary' | 'info'> = {
  planning: 'secondary',
  active: 'success',
  on_hold: 'warning',
  completed: 'info',
  cancelled: 'destructive',
  archived: 'secondary',
};

const priorityVariant: Record<string, 'default' | 'success' | 'warning' | 'destructive' | 'secondary'> = {
  low: 'secondary',
  medium: 'default',
  high: 'warning',
  urgent: 'destructive',
};

export function ProjectsOverview() {
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['project-dashboard'],
    queryFn: async () => {
      const result = await api.getProjectDashboard();
      return result.data as DashboardData;
    },
  });

  const summary = data?.summary;
  const recentProjects = data?.recentProjects || [];
  const milestones = data?.milestones || [];
  const overdueTasks = data?.overdueTasks || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Project Management</h1>
          <p className="text-muted-foreground">Overview of projects, tasks, and resource utilization</p>
        </div>
        <Button onClick={() => navigate('/projects/list/new')}>
          <Plus className="h-4 w-4 mr-2" />
          New Project
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Projects</CardTitle>
            <FolderKanban className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary?.activeProjects || 0}</div>
            <p className="text-xs text-muted-foreground">
              {summary?.totalProjects || 0} total projects
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Tasks</CardTitle>
            <ListTodo className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{summary?.completedTasks || 0} / {summary?.totalTasks || 0}</div>
            <p className="text-xs text-muted-foreground">
              {summary?.overdueTasks || 0} overdue
              {(summary?.overdueTasks || 0) > 0 && (
                <span className="text-destructive font-medium"> (action needed)</span>
              )}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Hours Logged</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{(summary?.totalLoggedHours || 0).toFixed(1)}h</div>
            <p className="text-xs text-muted-foreground">
              {(summary?.billableHours || 0).toFixed(1)}h billable ({(summary?.utilizationRate || 0).toFixed(0)}% utilization)
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Billable Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(summary?.billableAmount || 0)}</div>
            <p className="text-xs text-muted-foreground">
              {summary?.openRisks || 0} open risks
              {(summary?.highRisks || 0) > 0 && (
                <span className="text-destructive font-medium"> ({summary?.highRisks} high)</span>
              )}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Active Projects */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Active Projects</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => navigate('/projects/list')}>
              View All <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </CardHeader>
          <CardContent>
            {recentProjects.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <FolderKanban className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No projects yet</p>
                <Button variant="outline" size="sm" className="mt-2" onClick={() => navigate('/projects/list/new')}>
                  Create your first project
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {recentProjects.map((project) => (
                  <div
                    key={project.id}
                    className="p-3 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => navigate(`/projects/list/${project.id}`)}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs text-muted-foreground">#{project.projectNumber}</span>
                        <span className="font-medium">{project.name}</span>
                      </div>
                      <Badge variant={statusVariant[project.status] || 'secondary'}>{project.status.replace('_', ' ')}</Badge>
                    </div>
                    <div className="flex items-center justify-between text-xs text-muted-foreground mb-2">
                      <span>{project.accountName || 'Internal'}</span>
                      <span>{project.completedTaskCount}/{project.taskCount} tasks</span>
                    </div>
                    <Progress value={project.completionPct} className="h-1.5" />
                    <div className="flex justify-between text-xs text-muted-foreground mt-1">
                      <span>{project.completionPct.toFixed(0)}% complete</span>
                      <span>{project.totalLoggedHours.toFixed(1)}h logged</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Upcoming Milestones */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-4 w-4" />
                Upcoming Milestones
              </CardTitle>
            </CardHeader>
            <CardContent>
              {milestones.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No upcoming milestones</p>
              ) : (
                <div className="space-y-3">
                  {milestones.map((ms) => (
                    <div key={ms.id} className="flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 mt-0.5 text-muted-foreground" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{ms.name}</p>
                        <p className="text-xs text-muted-foreground">{ms.projectName}</p>
                        <p className="text-xs text-muted-foreground">{formatDate(ms.milestoneDate)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Overdue Tasks */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-destructive" />
                Overdue Tasks
              </CardTitle>
            </CardHeader>
            <CardContent>
              {overdueTasks.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No overdue tasks</p>
              ) : (
                <div className="space-y-3">
                  {overdueTasks.map((task) => (
                    <div key={task.id} className="flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 mt-0.5 text-destructive" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{task.title}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <Badge variant={priorityVariant[task.priority] || 'secondary'} className="text-[10px]">
                            {task.priority}
                          </Badge>
                          <span className="text-xs text-muted-foreground">{task.projectName}</span>
                        </div>
                        <p className="text-xs text-destructive">Due {formatDate(task.dueDate)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
